import { useMemo } from "react"

import useHasAccess from "@hooks/useHasAccess"
import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import type { MenuItemType } from "@organisms/Menu/Menu.types"

import { BILLS_ROUTES } from "@routes/bills"
import { CLIENTS_ROUTES } from "@routes/clients"
import { CREDIT_NOTES_ROUTES } from "@routes/creditNotes"
import { ESTIMATES_ROUTES } from "@routes/estimates"
import { INVOICES_ROUTES } from "@routes/invoices"
import { JOBS_ROUTES } from "@routes/jobs"
import { PRICEBOOK_ITEMS_ROUTES } from "@routes/pricebook-items"
import { PURCHASE_ORDER_ROUTES } from "@routes/purchase-orders"
import { VENDOR_ROUTES } from "@routes/vendor"

import { APIDeckTaxStrategies, AccountingIntegrations } from "@constants/choices"
import { objectMap } from "@constants/objectMap"
import { PERMISSION_LEVEL } from "@constants/permissionLevel"

/**
 * Return the "Create" menu's items, gated by the user's entitlements and permissions.
 *
 * `submenuClassName` styles the sub-menus, which render outside the menu's own content.
 */
export default function useCreateMenuItems(submenuClassName?: string) {
    const { user } = useUser()

    const { hasEntitlement, hasPermission } = useHasAccess()

    const isUsingTaxRates =
        user?.service_company?.use_taxes &&
        !(
            user?.service_company?.accounting_integration === AccountingIntegrations.apideck &&
            user?.service_company?.apideck_tax_strategy !== APIDeckTaxStrategies.normal
        )

    const [
        estimateCreateRoute,
        jobCreateRoute,
        invoiceCreateRoute,
        creditNoteCreateRoute,
        purchaseOrderCreateRoute,
        billCreateRoute,
        vendorCreateRoute,
        clientCreateRoute,
        pricebookItemServiceCreateRoute,
        pricebookItemPartOrMaterialCreateRoute,
        pricebookItemMiscellaneousCreateRoute,
        pricebookItemDiscountCreateRoute,
        pricebookItemTaxRatesCreateRoute,
    ] = useSlugExtractor([
        ESTIMATES_ROUTES.CREATE,
        JOBS_ROUTES.CREATE,
        INVOICES_ROUTES.CREATE,
        CREDIT_NOTES_ROUTES.CREATE,
        PURCHASE_ORDER_ROUTES.CREATE,
        BILLS_ROUTES.CREATE,
        VENDOR_ROUTES.CREATE,
        CLIENTS_ROUTES.CREATE,
        PRICEBOOK_ITEMS_ROUTES.SERVICE.CREATE,
        PRICEBOOK_ITEMS_ROUTES.PART_OR_MATERIAL.CREATE,
        PRICEBOOK_ITEMS_ROUTES.MISCELLANEOUS.CREATE,
        PRICEBOOK_ITEMS_ROUTES.DISCOUNT.CREATE,
        PRICEBOOK_ITEMS_ROUTES.TAX_RATES.CREATE,
    ])

    return useMemo(() => {
        const items: MenuItemType[] = []

        if (
            hasEntitlement("entitlement_estimates_enabled") &&
            hasPermission("estimates_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: objectMap.estimate.icon,
                path: estimateCreateRoute,
                title: objectMap.estimate.label,
            })
        }

        if (
            hasEntitlement("entitlement_jobs_enabled") &&
            hasPermission("jobs_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            // TODO: Re-enable create new job series CTA (the Series row's onClick closes the caller's menu
            // and opens its JobSeriesCreateDialog, so the caller has to hand that in). Consider giving
            // objectMap.job_series the layer-group icon and the "Series" label so the row reads them from there.
            // items.push({
            //     icon: objectMap.job.icon,
            //     title: objectMap.job.label,
            //     submenu: {
            //         header: { title: objectMap.job.label },
            //         className: submenuClassName,
            //         items: [
            //             {
            //                 type: "group",
            //                 items: [
            //                     {
            //                         icon: objectMap.job.icon,
            //                         title: objectMap.job.label,
            //                         path: jobCreateRoute,
            //                     },
            //                     {
            //                         icon: faIcon({ name: "layer-group", style: "regular" }),
            //                         title: "Series",
            //                         onClick: () => {
            //                             setIsOpen(false)
            //                             setIsSeriesDialogOpen(true)
            //                         },
            //                     },
            //                 ],
            //             },
            //         ],
            //     },
            // })
            items.push({
                icon: objectMap.job.icon,
                path: jobCreateRoute,
                title: objectMap.job.label,
            })
        }

        if (
            hasEntitlement("entitlement_invoices_enabled") &&
            hasPermission("invoices_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: objectMap.invoice.icon,
                title: objectMap.invoice.label,
                ...(user?.service_company?.feature_credit_notes_enabled
                    ? {
                          submenu: {
                              header: { title: objectMap.invoice.label },
                              className: submenuClassName,
                              items: [
                                  {
                                      type: "group",
                                      items: [
                                          {
                                              icon: objectMap.invoice.icon,
                                              title: objectMap.invoice.label,
                                              path: invoiceCreateRoute,
                                          },
                                          {
                                              icon: objectMap.credit_note.icon,
                                              title: objectMap.credit_note.label,
                                              path: creditNoteCreateRoute,
                                          },
                                      ],
                                  },
                              ],
                          },
                      }
                    : { path: invoiceCreateRoute }),
            })
        }

        if (
            hasEntitlement("entitlement_purchase_orders_enabled") &&
            hasPermission("purchase_orders_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: objectMap.purchase_order.icon,
                path: purchaseOrderCreateRoute,
                title: objectMap.purchase_order.label,
            })
        }

        if (
            hasEntitlement("entitlement_bills_enabled") &&
            hasPermission("bills_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: objectMap.bill.icon,
                path: billCreateRoute,
                title: objectMap.bill.label,
            })
        }

        if (hasPermission("vendors_create_permission", PERMISSION_LEVEL.FULL)) {
            items.push({
                icon: objectMap.vendor.icon,
                path: vendorCreateRoute,
                title: objectMap.vendor.label,
            })
        }

        if (hasPermission("clients_create_permission", PERMISSION_LEVEL.FULL)) {
            items.push({
                icon: objectMap.client.icon,
                path: clientCreateRoute,
                title: objectMap.client.label,
            })
        }

        if (hasPermission("pricebook_create_permission", PERMISSION_LEVEL.FULL)) {
            items.push({
                title: objectMap.pricebook_item.label,
                icon: objectMap.pricebook_item.icon,
                submenu: {
                    header: { title: objectMap.pricebook_item.label },
                    className: submenuClassName,
                    items: [
                        {
                            type: "group",
                            items: [
                                {
                                    icon: objectMap.labor.icon,
                                    title: "Service",
                                    path: pricebookItemServiceCreateRoute,
                                },
                                {
                                    icon: objectMap.product.icon,
                                    title: "Part or Material",
                                    path: pricebookItemPartOrMaterialCreateRoute,
                                },
                                {
                                    icon: objectMap.other.icon,
                                    title: "Other",
                                    path: pricebookItemMiscellaneousCreateRoute,
                                },
                                {
                                    icon: objectMap.discount.icon,
                                    title: "Discount",
                                    path: pricebookItemDiscountCreateRoute,
                                },
                                ...(isUsingTaxRates
                                    ? [
                                          {
                                              icon: objectMap.tax_rate.icon,
                                              title: "Tax Rate",
                                              path: pricebookItemTaxRatesCreateRoute,
                                          } as const,
                                      ]
                                    : []),
                            ],
                        },
                    ],
                },
            })
        }

        return items
    }, [
        hasEntitlement,
        hasPermission,
        isUsingTaxRates,
        user?.service_company?.feature_credit_notes_enabled,
        submenuClassName,
        estimateCreateRoute,
        jobCreateRoute,
        invoiceCreateRoute,
        creditNoteCreateRoute,
        purchaseOrderCreateRoute,
        billCreateRoute,
        vendorCreateRoute,
        clientCreateRoute,
        pricebookItemServiceCreateRoute,
        pricebookItemPartOrMaterialCreateRoute,
        pricebookItemMiscellaneousCreateRoute,
        pricebookItemDiscountCreateRoute,
        pricebookItemTaxRatesCreateRoute,
    ])
}
